import { Option } from "@inrev/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { prefersReducedMotion, SCROLL_SETTLE_MS } from "./autoScroll";
import type { Highlight } from "./highlights";
import type { DocumentViewerController } from "./useDocumentViewer";

const PULSE_MS = 900;
const REDUCED_MOTION_PULSE_MS = 1500;

/** One run of the halo around the selected field; a new `key` restarts it. */
export type Pulse = { key: number; durationMs: number };

type UseFieldSelectionOptions = {
	highlights: readonly Highlight[];
	activeFieldId: Option<string>;
	/** Whether the document is on screen; nothing scrolls before it is. */
	ready: boolean;
	scrollToRects: DocumentViewerController["scrollToRects"];
};

/**
 * Follows the selected field: scrolls to its first highlight when the selection changes, then
 * pulses it once the scroll lands. Re-rendering with the same selection never scrolls again.
 */
export const useFieldSelection = ({
	highlights,
	activeFieldId,
	ready,
	scrollToRects,
}: UseFieldSelectionOptions) => {
	const [pulse, setPulse] = useState<Option<Pulse>>(undefined);
	const pulseTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
	/** The field the viewer last scrolled to, so selecting it again on a re-render doesn't. */
	const scrolledFor = useRef<Option<string>>(undefined);

	const highlightsByField = useMemo(() => {
		const byField = new Map<string, Highlight[]>();
		for (const highlight of highlights) {
			const fieldHighlights = byField.get(highlight.fieldId) ?? [];
			fieldHighlights.push(highlight);
			byField.set(highlight.fieldId, fieldHighlights);
		}
		return byField;
	}, [highlights]);

	const startPulse = useCallback((delay: number) => {
		for (const timer of pulseTimers.current) clearTimeout(timer);
		const durationMs = prefersReducedMotion() ? REDUCED_MOTION_PULSE_MS : PULSE_MS;
		pulseTimers.current = [
			setTimeout(
				() => setPulse((current) => ({ key: (current?.key ?? 0) + 1, durationMs })),
				delay,
			),
			setTimeout(() => setPulse(undefined), delay + durationMs),
		];
	}, []);
	useEffect(
		() => () => {
			for (const timer of pulseTimers.current) clearTimeout(timer);
		},
		[],
	);

	/**
	 * Scrolls to the field's first highlight and pulses it, even when it is already selected.
	 * Does nothing before the document is on screen.
	 */
	const scrollToField = useCallback(
		(fieldId: string) => {
			const fieldHighlights = highlightsByField.get(fieldId);
			if (!fieldHighlights || fieldHighlights.length === 0) return;
			const firstPage = Math.min(...fieldHighlights.map((highlight) => highlight.page));
			const rects = fieldHighlights
				.filter((highlight) => highlight.page === firstPage)
				.flatMap((highlight) => highlight.rects);
			const scrolled = scrollToRects(firstPage, rects, (moved) =>
				startPulse(moved ? SCROLL_SETTLE_MS : 0),
			);
			if (scrolled) scrolledFor.current = fieldId;
		},
		[highlightsByField, scrollToRects, startPulse],
	);

	// A replaced document opens on its first page, so the selected field needs scrolling to again.
	useEffect(() => {
		if (Option.isNone(activeFieldId) || !ready) {
			scrolledFor.current = undefined;
			return;
		}
		if (scrolledFor.current !== activeFieldId) scrollToField(activeFieldId);
	}, [activeFieldId, ready, scrollToField]);

	const activeHighlight = Option.flatMap(activeFieldId, (fieldId) =>
		highlightsByField.get(fieldId)?.at(0),
	);

	return { pulse, activeHighlight, scrollToField };
};
